/**
 * Synchronizes the i18next language with the user's Firestore preference.
 *
 * Cascade:
 * 1. Firestore preferences.language (if logged in and set)
 * 2. localStorage / navigator (handled by i18next-browser-languagedetector)
 *
 * When the user switches language via the LanguageSwitcher:
 * - i18n.changeLanguage() updates the app
 * - The switcher writes to Firestore preferences.language
 * - localStorage is updated automatically by i18next
 *
 * Initial backfill:
 * - On the first authenticated session of a user whose Firestore doc has NO
 *   preferences.language (e.g. created by the onUserCreated backend trigger,
 *   which never sees the browser language), we persist the currently detected
 *   i18n.language so the BACKEND (emails, product prompts) knows the language.
 *   Without this, a German-browser user gets the de-CH UI but keeps receiving
 *   French emails because preferences.language stays empty until a manual
 *   LanguageSwitcher click.
 */
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useUserProfile } from './useUserProfile';
import { SUPPORTED_LANGUAGES, type AppLanguage } from '../i18n';

/**
 * Normalize an arbitrary i18n.language value to a supported AppLanguage.
 * i18next already maps de/de-DE/de-AT → de-CH via fallbackLng, but the raw
 * resolved language can still be a region variant (e.g. 'en-US', 'fr-FR') or
 * an unsupported code. We coerce to an exact SUPPORTED_LANGUAGES member so the
 * backend always reads a known value.
 */
const normalizeToSupported = (lang: string | undefined): AppLanguage => {
  if (!lang) return 'fr';
  if ((SUPPORTED_LANGUAGES as readonly string[]).includes(lang)) {
    return lang as AppLanguage;
  }
  const base = lang.split('-')[0].toLowerCase();
  if (base === 'de') return 'de-CH';
  if ((SUPPORTED_LANGUAGES as readonly string[]).includes(base)) {
    return base as AppLanguage;
  }
  return 'fr';
};

export const useLanguageSync = () => {
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const { profileData, loading } = useUserProfile();

  // Guards against re-writing the backfill within the same session (the
  // onSnapshot update will re-render before the new field is reflected, and
  // even after, this prevents any redundant write attempt).
  const didBackfillRef = useRef(false);

  // 1. Firestore → i18n: apply a stored preference to the running app.
  useEffect(() => {
    if (!user || !profileData?.preferences?.language) return;

    const firestoreLang = profileData.preferences.language;
    if (
      (SUPPORTED_LANGUAGES as readonly string[]).includes(firestoreLang) &&
      i18n.language !== firestoreLang
    ) {
      i18n.changeLanguage(firestoreLang);
    }
  }, [user, profileData?.preferences?.language, i18n]);

  // 2. i18n → Firestore (initial backfill only): persist the detected language
  //    when the user's doc has loaded but carries no language preference yet.
  useEffect(() => {
    if (didBackfillRef.current) return;
    if (!user || loading) return;
    // profileData === null means the snapshot has not resolved yet.
    if (!profileData) return;
    // Only backfill when the value is genuinely absent/empty — never overwrite
    // an existing explicit choice (no double source of truth).
    if (profileData.preferences?.language) return;

    const detected = normalizeToSupported(i18n.language);
    didBackfillRef.current = true; // set before the async write to avoid races

    const userDocRef = doc(db, 'users', user.uid);
    updateDoc(userDocRef, { 'preferences.language': detected }).catch(() => {
      // Silent fail — localStorage already holds the detection; the next
      // session will retry. Reset the guard so a later render can re-attempt.
      didBackfillRef.current = false;
    });
  }, [user, loading, profileData, i18n.language]);

  // Reset the backfill guard when the authenticated user changes (logout/login
  // as a different account) so each account gets its own one-shot backfill.
  useEffect(() => {
    didBackfillRef.current = false;
  }, [user?.uid]);

  // Also update the <html lang> attribute
  useEffect(() => {
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);
};
